import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle2 } from 'lucide-react';

const SuccessPyramidAmina = () => {
  const { t } = useLanguage();

  const levels = [
    {
      title: t.pyramid.level1Title,
      subtitle: t.pyramid.level1Subtitle,
      points: [
        t.pyramid.level1Point1,
        t.pyramid.level1Point2,
        t.pyramid.level1Point3,
        t.pyramid.level1Point4
      ],
      width: 'w-full max-w-2xl' // Top - narrowest
    },
    {
      title: t.pyramid.level2Title,
      subtitle: t.pyramid.level2Subtitle,
      points: [
        t.pyramid.level2Point1,
        t.pyramid.level2Point2,
        t.pyramid.level2Point3,
        t.pyramid.level2Point4
      ],
      width: 'w-full max-w-3xl' // Middle
    },
    {
      title: t.pyramid.level3Title,
      subtitle: t.pyramid.level3Subtitle,
      points: [
        t.pyramid.level3Point1,
        t.pyramid.level3Point2,
        t.pyramid.level3Point3,
        t.pyramid.level3Point4
      ],
      width: 'w-full max-w-4xl' // Base - widest
    }
  ];

  return (
    <section id="pyramid" className="py-32 relative overflow-hidden w-full min-w-0">
      {/* Background with subtle glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0f1f3a]/20 to-transparent"></div>

      <div className="container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header - Serif font like Amina */}
          <div className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif-heading gradient-text-gold mb-6">
              {t.pyramid.title}
            </h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto font-light">
              {t.pyramid.subtitle}
            </p>
          </div>

          {/* Pyramid Stack - Centered & Narrowing */}
          <div className="flex flex-col items-center gap-0 mt-16">
            {levels.map((level, index) => (
              <div
                key={index}
                className={`${level.width} max-w-full min-w-0 overflow-hidden transition-all duration-500 hover:scale-[1.02]`}
                style={{
                  marginBottom: index < levels.length - 1 ? '-1px' : '0'
                }}
              >
                <div
                  className="relative group px-4 sm:px-8"
                  style={{
                    background: 'rgba(15, 31, 58, 0.6)',
                    border: '1px solid rgba(212, 165, 116, 0.3)',
                    borderTop: index === 0 ? '1px solid rgba(212, 165, 116, 0.3)' : 'none',
                    paddingTop: index === 0 ? '2.5rem' : '2rem',
                    paddingBottom: '2.5rem',
                    boxShadow: '0 0 40px rgba(212, 165, 116, 0.15), inset 0 0 20px rgba(212, 165, 116, 0.05)'
                  }}
                >
                  {/* Title Section */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider mb-2"
                        style={{ color: '#d4a574' }}>
                      {level.title}
                    </h3>
                    <p className="text-sm text-white/60 uppercase tracking-wider font-light">
                      {level.subtitle}
                    </p>
                  </div>

                  {/* Points Grid */}
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {level.points.map((point, pointIndex) => (
                      <div key={pointIndex} className="flex items-start gap-3 min-w-0">
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#d4a574' }} />
                        <span className="text-sm text-white/70 font-light leading-relaxed break-words min-w-0">{point}</span>
                      </div>
                    ))}
                  </div>

                  {/* Hover glow effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                       style={{
                         boxShadow: '0 0 60px rgba(212, 165, 116, 0.25)'
                       }}>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Foundation Label */}
          <div className="text-center mt-8 px-2">
            <div className="inline-block max-w-full px-6 sm:px-8 py-4 rounded-lg"
                 style={{
                   background: 'rgba(15, 31, 58, 0.6)',
                   border: '1px solid rgba(212, 165, 116, 0.2)',
                   boxShadow: '0 0 20px rgba(212, 165, 116, 0.1)'
                 }}>
              <p className="text-lg uppercase tracking-wider font-semibold break-words px-1" style={{ color: '#d4a574' }}>
                {t.pyramid.level3Subtitle}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuccessPyramidAmina;
